import { useMutation } from '@apollo/client'
import { Button, HStack, KeychaineInput, Modal, Tag, VStack } from '@chaine/keychaine'
import React, { useState } from 'react'

import { Toast } from '../../../_shared/components/toast'
import { StringUtils } from '../../../_utils'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_EMAIL_RATE_CONFIRMATION } from '../data/mutations'

interface EmailRateConfirmationResponse {
  enableEmailRateConfirmationApp: {
    rateConfirmationEmail: string
  }
}

/**
 * Modal to enable the email rate confirmation app
 * @param {boolean} props.isOpen - determines whether the modal is open or closed
 * @param {function} props.onClose - A callback function to close the modal.
 * @param {AppType} props.appType - The type of the app being configured.
 * @param {function} props.refetchApps - A function to refetch app data.
 */
const EnableEmailRateConfirmationModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, refetchApps } = props
  const [emailInput, setEmailInput] = useState<string>('')
  const [emails, setEmails] = useState<string[]>([])

  // It handles changes to the input for emails. The emails are separated by commas and are stored in an array.
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value
    setEmailInput(input)
    const parsedEmails = input.split(',').map((email) => email.trim())
    setEmails(parsedEmails)
  }
  const { showToast } = Toast()

  const [saveStateParam, { loading }] = useMutation<EmailRateConfirmationResponse>(ENABLE_EMAIL_RATE_CONFIRMATION, {
    onCompleted: (data: EmailRateConfirmationResponse) => {
      showToast({
        status: 'success',
        title: `Email for sending shipment updates has been successfully configured. You can use this email: ${data.enableEmailRateConfirmationApp.rateConfirmationEmail}`
      })
      refetchApps()
      handleCancel()
    },
    onError: (err) => {
      showToast({
        status: 'error',
        title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
      })
    }
  })

  // The function validates the email list, shows an error toast for invalid or duplicate emails, and triggers the mutation.
  const handleSaveDetails = () => {
    // Validate each email in the list
    const invalidEmails = emails.filter((email) => !StringUtils.isEmail(email))

    // If any invalid emails are found, show an error toast and return
    if (invalidEmails.length > 0) {
      showToast({
        status: 'error',
        title: `Invalid email addresses: ${invalidEmails.join(', ')}`
      })
      return
    }

    const isDuplicate = StringUtils.hasDuplicateEmails(emails)

    if (isDuplicate) {
      showToast({
        status: 'error',
        title: 'Duplicate email addresses found'
      })
      return
    }
    saveStateParam({ variables: { allowedEmailAddresses: emails, appType: appType } })
  }

  // This function is called to close the modal and reset the state.
  const handleCancel = () => {
    onClose()
    setEmailInput('')
    setEmails([])
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleCancel}
        title={`What emails will the rate confirmations come from?`}
        size="lg"
        subtitle={
          'Enter one or more emails that will send the rate confirmations. We will provide you with a unique email where you can forward them to automatically create the load in Chaine.'
        }
        footer={
          <>
            <Button variant={'primary'} mr={3} onClick={handleSaveDetails} isLoading={loading}>
              Enable
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
          </>
        }
      >
        <>
          <VStack spacing={4} align="stretch">
            <KeychaineInput
              placeholder="Enter one or more emails separated by commas."
              onChange={handleChange}
              name={'allowedEmailAddresses'}
              value={emailInput}
            />
            <HStack spacing={2} flexWrap="wrap" gap={2}>
              {emails.map((email, index) => {
                const isEmail = StringUtils.isEmail(email || '')
                if (email.trim() === '') {
                  return null
                }
                return (
                  <Tag size={'sm'} key={index} colorScheme={isEmail ? 'gray' : 'red'}>
                    {email}
                  </Tag>
                )
              })}
            </HStack>
          </VStack>
        </>
      </Modal>
    </>
  )
})

EnableEmailRateConfirmationModal.displayName = 'EnableEmailRateConfirmationModal'

export { EnableEmailRateConfirmationModal }
